import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FiltroClientesComponent } from 'app/components/filtro-clientes/filtro-clientes.component';
import { Equipamento, Equipamentos } from 'app/interfaces/Equipamento';
import { FiltroEquipamentosComponent } from 'app/components/filtro-equipamentos/filtro-equipamentos.component';
import { TelaComponent } from './tela/tela.component';
import { ComunicadorService } from 'app/services/comunicador/comunicador.service';
import { ConfiguracoesConnect, EntidadeConfiguracao, ExibicoesConfiguracoes } from 'app/interfaces/configuracoes-connect';
import { NgIf } from '@angular/common';
import { ConfiguracoesService } from 'app/services/agview-connect/configuracoes/configuracoes.service';
import { concatMap, map, Observable, of, Subject, Subscription, tap } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarComponent } from './snackbar/snackbar.component';
import { ConfiguracoesSyncService } from 'app/services/agview-connect/configuracoes-sync/configuracoes-sync.service';
import { WebsocketService } from 'app/services/websocket/websocket.service';
import { AutenticacaoConfiguracaoLicencaService } from 'app/services/agview-connect/autenticacao-configuracao-licenca/autenticacao-configuracao-licenca.service';
import { WebsocketTipo } from 'app/interfaces/websocket';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-agview-connect',
  standalone: true,
  imports: [TelaComponent, FiltroClientesComponent, FiltroEquipamentosComponent, NgIf, TranslocoModule],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './agview-connect.component.html',
  styleUrl: './agview-connect.component.css'
})
export class AgviewConnectComponent implements OnInit, OnDestroy {
  public static readonly PLACEHOLDER_CARREGANDO: string = 'Carregando equipamentos...';
  public static readonly PLACEHOLDER_SELECIONE: string = 'Selecione um equipamento...';
  public static readonly PLACEHOLDER_SEM_EQUIPAMENTOS: string = 'Sem equipamentos';
  public readonly TIPO_COMUNICADOR: string = 'AGViewConnect';
  public readonly EVENTO: string = 'CanalPorAGViewConnectEvent';
  public equipamentoId: number | null = Number(localStorage.getItem('equipamentoId') ?? '');
  public clienteId: number | null = Number(localStorage.getItem('clienteId') ?? '');
  public equipamentos: Equipamentos = [];
  public loadingEquipamentos: boolean = false;
  public podeFiltrarInformacoes: boolean = false;
  public carregouPelaPrimeiraVez: boolean = false;
  public carregandoConfiguracoes: boolean = false;
  public placeholderEquipamentos: string = AgviewConnectComponent.PLACEHOLDER_SELECIONE;
  public equipamentoFiltrado: Equipamento | undefined;
  public configuracoesCadastradas!: ConfiguracoesConnect | null;
  public canal: string = '';
  public mostrarBadgeEnviosPendentes = false;
  public configuracoesPendentes: Record<string, ExibicoesConfiguracoes> | null = null;
  public eventoSub: Subscription | undefined;
  private readonly destroy$ = new Subject<void>();

  private readonly SNACKBAR_PREFIX = 'COMPONENTS.AGVIEW_CONNECT.SNACKBAR';

  constructor(
    private comunicadorService: ComunicadorService,
    private configuracoesService: ConfiguracoesService,
    private websocketService: WebsocketService,
    private snackBar: MatSnackBar,
    private configuracoesSyncService: ConfiguracoesSyncService,
    private autenticacaoConfiguracaoLicencaService: AutenticacaoConfiguracaoLicencaService,
    private translocoService: TranslocoService
  ) { }

  ngOnInit(): void {
    this.fecharConexaoWebsocket();
  }

  ngOnDestroy() {
    this.fecharConexaoWebsocket();
    this.destroy$.next();
    this.destroy$.complete();
  }

  public selecionarEquipamento(equipamentoId: number): void {
    if (!equipamentoId || equipamentoId == 0) {
      this.equipamentoId = null;
      localStorage.removeItem('equipamentoId');
    } else {
      this.equipamentoId = equipamentoId;
    }

    this.habilitarBotaoFiltrar();
  }

  public buscarEquipamentos(clienteId: number): void {
    this.resetarEstado();
    this.loadingEquipamentos = true;
    this.clienteId = clienteId;
  }

  private resetarEstado(): void {
    this.equipamentoId = null;
    this.podeFiltrarInformacoes = false;
  }

  public habilitarBotaoFiltrar(): void {
    this.podeFiltrarInformacoes = Boolean(this.equipamentoId);
  }

  public obterEquipamentos(equipamentos: Equipamentos): void {
    this.equipamentos = equipamentos;
  }

  public onFiltrar(): void {
    this.autenticacaoConfiguracaoLicencaService.logout();
    this.configuracoesCadastradas = null;
    this.carregandoConfiguracoes = true;
    this.configuracoesPendentes = null;
    localStorage.setItem('equipamentoId', (this.equipamentoId ?? 0).toString());
    this.equipamentoFiltrado = this.equipamentos.find((equipamento: Equipamento) => equipamento.sr_id == Number(this.equipamentoId));
    if (this.equipamentoFiltrado) {
      let imei: string | undefined = this.equipamentoFiltrado.agview_connect?.vc_imei;

      if (imei) {
        this.comunicadorService.buscarConfiguracoes(imei).subscribe((configuracoes: ConfiguracoesConnect) => {
          this.configuracoesCadastradas = configuracoes;
          this.configuracoesCadastradas.License = {
            licenseCode: this.configuracoesCadastradas.License as any
          };
          this.configuracoesService.atualizar(configuracoes);
          this.carregandoConfiguracoes = false;
          this.inscreverEventoWebsocket(imei);
          this.buscarEnviosPendentes().subscribe(() => { this.mostrarBadgeEnviosPendentes = !!this.configuracoesPendentes; });
          this.inscreverEventoNotificacaoEnvio();
        });
      }
    }
  }

  public inscreverEventoWebsocket(imei: string): void {
    this.fecharConexaoWebsocket();
    this.canal = `comunicador-${imei}`;

    this.eventoSub = this.websocketService.inscreverEvento(WebsocketTipo.Public, this.canal, this.EVENTO)
      .subscribe(dadosRecebidos => {
        if (!this.validarDados(dadosRecebidos)) return;

        const mensagem = dadosRecebidos.mensagem;
        const nomesEntidades = Object.keys(mensagem).filter(chave => chave !== 'id_envio');
        this.mostrarNotificacao('info', imei, nomesEntidades);
        for (const chave of Object.keys(mensagem)) {
          if (chave !== 'id_envio')
            if (chave == 'License') {
              mensagem[chave] = { licenseCode: mensagem[chave] };
            }
          this.processarMensagem(chave as EntidadeConfiguracao, mensagem[chave]);
        }
      });
  }

  public validarDados(dadosRecebidos: any): boolean {
    if (!dadosRecebidos || !dadosRecebidos.mensagem) return false;

    const { mensagem, destino } = dadosRecebidos;

    return (
      typeof mensagem === 'object' &&
      mensagem !== null &&
      Object.keys(mensagem).length > 0 &&
      destino === 'web'
    );
  }

  public processarMensagem(chave: EntidadeConfiguracao, dados: any): void {
    this.configuracoesService.atualizarPorEntidade(chave, dados);
  }

  public fecharConexaoWebsocket(): void {
    if (this.eventoSub) {
      this.eventoSub.unsubscribe();
      this.websocketService.cancelarInscricaoCanal(WebsocketTipo.Public, this.canal);
    }
  }

  public inscreverEventoNotificacaoEnvio(): void {
    this.configuracoesSyncService.aoDispararNotificacaoEnvio()
      .pipe(
        concatMap(() => this.buscarEnviosPendentes()),
        tap(() => {
          this.mostrarNotificacao('sucesso', this.equipamentoFiltrado?.agview_connect?.vc_imei);
        })
      )
      .subscribe();
  }

  public mostrarNotificacao(tipo: 'info' | 'sucesso', imei?: string, entidadesConfiguracoes?: string[]): void {
    const t = (key: string, params?: object) => this.translocoService.translate(key, params);
    const frota = this.equipamentoFiltrado?.vc_frota;

    const alerta = {
      type: 'info',
      message: t(`${this.SNACKBAR_PREFIX}.SYNC_TITLE`),
      subtext: t(`${this.SNACKBAR_PREFIX}.SYNC_SUBTEXT`, { frota }),
    };

    const sucesso = {
      type: 'sucesso',
      message: t(`${this.SNACKBAR_PREFIX}.SAVE_TITLE`),
      subtext: t(`${this.SNACKBAR_PREFIX}.SAVE_SUBTEXT`, { frota }),
    };

    const notificacao = tipo == 'sucesso' ? sucesso : alerta;

    const ref = this.snackBar.openFromComponent(SnackbarComponent, {
      data: {
        message: notificacao.message,
        subtext: notificacao.subtext,
        duration: 4000,
        type: notificacao.type,
        imei: imei ?? null,
        entidadesConfiguracoes: entidadesConfiguracoes ?? null,
        configuracoesPendentes: this.configuracoesPendentes ?? null
      },
      horizontalPosition: 'right',
      verticalPosition: 'bottom',
      panelClass: ['custom-snackbar', notificacao.type]
    });

    if (tipo === 'sucesso') {
      ref.afterDismissed().subscribe(() => {
        this.mostrarBadgeEnviosPendentes = true;
      });
    }
  }

  public abrirEnviosPendentes(): void {
    if (this.configuracoesPendentes)
      this.mostrarConfiguracoesNaTela(this.configuracoesPendentes);
  }

  public buscarEnviosPendentes(): Observable<void> {
    if (!this.equipamentoFiltrado?.agview_connect?.vc_imei) {
      return of(undefined);
    }

    const imei = this.equipamentoFiltrado.agview_connect.vc_imei;

    return this.comunicadorService.buscarEnviosConfiguracoes(imei, 'pendente').pipe(
      tap((response) => {
        if (response && response.length > 0) {
          const todasConfigs = response.reduce((acc: Record<string, any>, curr: any) => {
            return { ...acc, ...curr.Configs };
          }, {});
          this.configuracoesPendentes = this.configuracoesService.mapearMultiplasConfiguracoesDeExibicao(todasConfigs);
        } else {
          this.mostrarBadgeEnviosPendentes = false;
        }
      }),
      map(() => void 0)
    );
  }

  private mostrarConfiguracoesNaTela(configuracoes: Record<string, ExibicoesConfiguracoes>): void {
    const titulo = 'Envios pendentes';
    this.configuracoesService.abrirModalConfiguracoes(titulo, configuracoes, 'sucesso');
  }
}